import React, { useMemo } from 'react';
import { Typography, Table, Row, Col } from 'antd';
import dayjs from 'dayjs';
import type { SimplifiedPendingOrder } from '@/services/PendingOrdersService';
import { formatCurrency } from '@/services/PendingOrdersService';

const { Text } = Typography;

interface PendingOrderDocumentProps {
  order: SimplifiedPendingOrder;
}

const PendingOrderDocument: React.FC<PendingOrderDocumentProps> = ({ order }) => {
  const toFiniteNumber = (value: unknown, fallback = 0): number => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };
  const pickMeaningfulDescription = (reference: string, ...candidates: unknown[]): string => {
    const normalizedReference = String(reference || '').trim().toUpperCase();
    for (const candidate of candidates) {
      const text = String(candidate || '').trim();
      if (!text) continue;
      if (normalizedReference && text.toUpperCase() === normalizedReference) continue;
      return text;
    }
    return '';
  };

  type FlatItemRow = {
    key: string;
    reference: string;
    short_reference: string;
    description: string;
    measure_unit: string;
    talla: string;
    quantity_ordered: number;
    unit_price: number;
    subtotal_value: number;
  };

  const flatItems = useMemo<FlatItemRow[]>(() => {
    const rows: FlatItemRow[] = [];
    (order.references || []).forEach((ref) => {
      (ref.items || []).forEach((item, idx) => {
        // Referencia "corta"/externa de SIESA (f120_descripcion_corta): la que
        // reconoce el cliente (D46003-905) frente a la interna (13001822-K).
        // Los pedidos del listado la traen como item_short_description.
        const shortReference = pickMeaningfulDescription(
          ref.reference,
          (item as any).short_reference,
          (ref as any).short_reference,
          (item as any).item_short_description,
          ref.item_short_description,
        );
        // Descripcion se queda con la descripcion real del item
        // (f120_descripcion). Antes tomaba primero la corta y la descripcion
        // generica se perdia; ahora cada una tiene su columna. La corta solo se
        // usa aqui como respaldo cuando no hay descripcion.
        const itemDescription =
          pickMeaningfulDescription(
            ref.reference,
            (item as any).item_description,
            (item as any).description,
            ref.description,
          ) || shortReference;
        // Sin repetir el mismo texto en dos columnas.
        const dedupedShortReference =
          shortReference && shortReference.trim().toUpperCase() === itemDescription.trim().toUpperCase()
            ? ''
            : shortReference;
        rows.push({
          key: `${ref.reference}-${item.item_extension || 'NA'}-${idx}`,
          reference: ref.reference,
          short_reference: dedupedShortReference || '-',
          description: itemDescription || '-',
          measure_unit: ref.measure_unit,
          talla: item.item_extension,
          quantity_ordered: item.quantity_ordered,
          unit_price: item.unit_price,
          subtotal_value: item.subtotal_value,
        });
      });
    });
    return rows;
  }, [order.references]);

  const observationsText = useMemo(() => {
    const direct = String((order as any).observations || (order as any).notes || '').trim();
    if (direct) return direct;

    const collected = new Set<string>();
    (order.references || []).forEach((ref: any) => {
      const referenceNote = String(ref?.notes || '').trim();
      if (referenceNote) collected.add(referenceNote);

      (ref?.items || []).forEach((item: any) => {
        const itemNote = String(item?.notes || '').trim();
        if (itemNote) collected.add(itemNote);
      });
    });

    return Array.from(collected).join('\n');
  }, [order]);

  const productColumns = useMemo(
    () => [
      {
        title: 'Referencia',
        dataIndex: 'reference',
        key: 'reference',
        width: 82,
        render: (text: string) => <Text style={{ fontSize: 12, fontWeight: 600 }}>{text}</Text>,
      },
      {
        title: 'Ref. Corta',
        dataIndex: 'short_reference',
        key: 'short_reference',
        width: 118,
        render: (text: string) => <Text style={{ fontSize: 12, fontWeight: 600 }}>{text}</Text>,
      },
      {
        title: 'Descripcion',
        dataIndex: 'description',
        key: 'description',
        render: (text: string) => <Text style={{ fontSize: 12 }}>{text}</Text>,
      },
      {
        title: 'Talla/Color',
        dataIndex: 'talla',
        key: 'talla',
        width: 72,
        align: 'center' as const,
        render: (text: string) => <Text style={{ fontSize: 12 }}>{text || '-'}</Text>,
      },
      {
        title: 'U.M.',
        dataIndex: 'measure_unit',
        key: 'measure_unit',
        width: 44,
        align: 'center' as const,
        render: (text: string) => <Text style={{ fontSize: 12 }}>{text}</Text>,
      },
      {
        title: 'Cant. Pedida',
        dataIndex: 'quantity_ordered',
        key: 'quantity_ordered',
        width: 72,
        align: 'right' as const,
        render: (qty: number) => <Text style={{ fontSize: 12 }}>{(qty || 0).toLocaleString()}</Text>,
      },
      {
        title: 'Precio Unit.',
        dataIndex: 'unit_price',
        key: 'unit_price',
        width: 88,
        align: 'right' as const,
        render: (val: number) => <Text style={{ fontSize: 12 }}>{formatCurrency(val || 0)}</Text>,
      },
      {
        title: 'Vlr Total',
        dataIndex: 'subtotal_value',
        key: 'subtotal_value',
        width: 98,
        align: 'right' as const,
        render: (val: number) => <Text style={{ fontSize: 13, fontWeight: 600 }}>{formatCurrency(val || 0)}</Text>,
      },
    ],
    [],
  );

  const subtotalValue = toFiniteNumber(
    order.document_subtotal_value,
    flatItems.reduce((sum, row) => sum + (row.subtotal_value || 0), 0),
  );
  const discountValue = toFiniteNumber(order.document_discount_value, 0);
  const taxValue = toFiniteNumber(order.document_tax_value, 0);
  const totalValue = toFiniteNumber(order.document_total_value, subtotalValue - discountValue + taxValue);

  const pagedItems = useMemo(() => {
    const regularItemsPerPage = 8;
    const lastPageMaxItems = 6;
    const pages: FlatItemRow[][] = [];

    for (let i = 0; i < flatItems.length; i += regularItemsPerPage) {
      pages.push(flatItems.slice(i, i + regularItemsPerPage));
    }

    if (pages.length === 0) pages.push([]);

    // Reserve extra space for totals/observations/footer on the last page.
    while (pages.length > 0 && pages[pages.length - 1].length > lastPageMaxItems) {
      const last = pages[pages.length - 1];
      const head = last.slice(0, lastPageMaxItems);
      const tail = last.slice(lastPageMaxItems);
      pages[pages.length - 1] = head;
      pages.push(tail);
    }

    return pages;
  }, [flatItems]);

  const pageCount = Math.max(1, pagedItems.length);

  const pageStyle: React.CSSProperties = {
    background: '#fff',
    width: '210mm',
    minHeight: '297mm',
    boxSizing: 'border-box',
    padding: '14mm',
    boxShadow: '0 2px 10px rgba(0,0,0,0.12)',
    fontFamily: "'Segoe UI', Arial, sans-serif",
    fontSize: 13,
    color: '#000',
    overflow: 'visible',
    WebkitFontSmoothing: 'antialiased',
    MozOsxFontSmoothing: 'grayscale' as any,
    imageRendering: 'auto',
  };

  const getUserName = (): string => {
    try {
      const sessionStr = localStorage.getItem('session');
      if (sessionStr) {
        const session = JSON.parse(sessionStr);
        return session?.user || 'Usuario';
      }
    } catch {}
    return 'Usuario';
  };

  const FieldCell = ({ label, value, bold = false }: { label: string; value: string | number; bold?: boolean }) => (
    <div style={{ display: 'flex', borderBottom: '1px solid #ccc', padding: '6px 0' }}>
      <Text style={{ fontSize: 12, color: '#666', width: 100, flexShrink: 0 }}>{label}:</Text>
      <Text style={{ fontSize: 14, fontWeight: bold ? 600 : 400, color: '#000' }}>{value}</Text>
    </div>
  );

  return (
    <>
      {pagedItems.map((pageItems, pageIndex) => {
        const isLast = pageIndex === pageCount - 1;

        return (
          <div
            key={pageIndex}
            data-pdf-page
            style={{
              ...pageStyle,
              marginBottom: 16,
              pageBreakAfter: isLast ? 'auto' : 'always',
            }}
          >
            <Row gutter={0} style={{ marginBottom: 20 }}>
              <Col span={16}>
                <div style={{ textAlign: 'center', paddingRight: 20 }}>
                  <Text style={{ fontSize: 22, fontWeight: 700, display: 'block', letterSpacing: 2 }}>DISENOS EXCLUSIVOS SAS</Text>
                  <Text style={{ fontSize: 13, display: 'block', marginTop: 4 }}>890922569-4</Text>
                  <Text style={{ fontSize: 12, color: '#444', display: 'block' }}>CALLE 17 43F 23</Text>
                  <Text style={{ fontSize: 12, color: '#444', display: 'block' }}>Medellin</Text>
                  <Text style={{ fontSize: 12, color: '#444', display: 'block' }}>Tel: 3540505</Text>
                </div>
              </Col>
              <Col span={8}>
                <div style={{ border: '2px solid #000', padding: 12 }}>
                  <Text style={{ fontSize: 20, fontWeight: 700, letterSpacing: 4, display: 'block', textAlign: 'center', marginBottom: 8 }}>
                    P E D I D O
                  </Text>
                  <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #000', paddingTop: 8 }}>
                    <Text style={{ fontSize: 12 }}>Numero:</Text>
                    <Text style={{ fontSize: 14, fontWeight: 700 }}>{order.orderNumber}</Text>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Text style={{ fontSize: 12 }}>Fecha:</Text>
                    <Text style={{ fontSize: 13 }}>{order.document_date || dayjs().format('DD/MM/YYYY')}</Text>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Text style={{ fontSize: 12 }}>Pagina:</Text>
                    <Text style={{ fontSize: 13 }}>{pageIndex + 1} de {pageCount}</Text>
                  </div>
                </div>
              </Col>
            </Row>

            <Row gutter={0} style={{ border: '1px solid #000', marginBottom: 16 }}>
              <Col span={12} style={{ borderRight: '1px solid #000', padding: 12 }}>
                <FieldCell label="Cliente" value={order.client_name || order.client} bold />
                <FieldCell label="NIT" value={order.client_nit || order.client || 'N/A'} />
                <FieldCell label="Sucursal" value={order.branch_name || order.branch || 'Principal'} />
              </Col>
              <Col span={12} style={{ padding: 12 }}>
                <FieldCell label="Forma pago" value={order.payment_condition || 'Credito'} />
                <FieldCell label="F. Entrega" value={order.delivery_date} bold />
                <FieldCell label="Vendedor" value={order.seller_name || getUserName() || order.seller || 'N/A'} />
                <FieldCell label="O. Compra" value={order.purchase_order || 'N/A'} />
              </Col>
            </Row>

            <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              <Table
                dataSource={pageItems}
                columns={productColumns}
                rowKey="key"
                size="small"
                pagination={false}
                bordered
                style={{ marginBottom: 16 }}
                rowClassName={() => 'doc-table-row'}
              />

              {/* Fill remaining space so every page keeps the same height visually */}
              <div style={{ flex: 1 }} />
            </div>

            {isLast && (
              <>
                <Row justify="end" style={{ marginBottom: 20 }}>
                  <Col span={12}>
                    <div style={{ border: '1px solid #000' }}>
                      <Row style={{ borderBottom: '1px solid #000' }}>
                        <Col span={12} style={{ padding: '8px 12px', borderRight: '1px solid #000' }}>
                          <Text style={{ fontSize: 13 }}>Sub Total:</Text>
                        </Col>
                        <Col span={12} style={{ padding: '8px 12px', textAlign: 'right' }}>
                          <Text style={{ fontSize: 15 }}>{formatCurrency(subtotalValue)}</Text>
                        </Col>
                      </Row>
                      <Row style={{ borderBottom: '1px solid #000' }}>
                        <Col span={12} style={{ padding: '8px 12px', borderRight: '1px solid #000' }}>
                          <Text style={{ fontSize: 13 }}>Descuento:</Text>
                        </Col>
                        <Col span={12} style={{ padding: '8px 12px', textAlign: 'right' }}>
                          <Text style={{ fontSize: 15 }}>{formatCurrency(discountValue)}</Text>
                        </Col>
                      </Row>
                      <Row style={{ borderBottom: '1px solid #000' }}>
                        <Col span={12} style={{ padding: '8px 12px', borderRight: '1px solid #000' }}>
                          <Text style={{ fontSize: 13 }}>Impuestos:</Text>
                        </Col>
                        <Col span={12} style={{ padding: '8px 12px', textAlign: 'right' }}>
                          <Text style={{ fontSize: 15 }}>{formatCurrency(taxValue)}</Text>
                        </Col>
                      </Row>
                      <Row style={{ background: '#f5f5f5' }}>
                        <Col span={12} style={{ padding: '10px 12px', borderRight: '1px solid #000' }}>
                          <Text style={{ fontSize: 15, fontWeight: 700 }}>TOTAL:</Text>
                        </Col>
                        <Col span={12} style={{ padding: '10px 12px', textAlign: 'right' }}>
                          <Text style={{ fontSize: 18, fontWeight: 700 }}>{formatCurrency(totalValue)}</Text>
                        </Col>
                      </Row>
                    </div>
                  </Col>
                </Row>

                {observationsText && (
                  <div style={{ border: '1px solid #000', padding: 12, marginBottom: 20 }}>
                    <Text style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>OBSERVACIONES:</Text>
                    <Text style={{ fontSize: 13, whiteSpace: 'pre-line' }}>{observationsText}</Text>
                  </div>
                )}

                <div style={{ marginTop: 30, paddingTop: 12, borderTop: '1px solid #ddd', textAlign: 'center' }}>
                  <Text style={{ fontSize: 12, color: '#444', display: 'block' }}>
                    Elaborado por: <Text strong>{getUserName()}</Text>
                  </Text>
                  <Text style={{ fontSize: 10, color: '#888', marginTop: 4, display: 'block' }}>
                    {dayjs().format('DD/MM/YYYY HH:mm')} - Sistema CRM
                  </Text>
                </div>
              </>
            )}
          </div>
        );
      })}
    </>
  );
};

export default PendingOrderDocument;
